import React from 'react'
import { object, string } from 'yup';
import { useFormik } from 'formik';

// Types
import { NewUserData } from '../../store/models/user';

// Constants
import { TEXT_LENGTH_LIMITS } from '../../constants/common';

// Components
import InputControl from '../InputControl/InputControl';
import InteractiveButton from '../InteractiveButton/InteractiveButton';

type ProfileInfoFormProps = {
  defaultFormData: {
    first_name: string;
    last_name: string;
  };
  onSubmit: (data: NewUserData) => Promise<void>;
};

const ProfileInfoForm: React.FC<ProfileInfoFormProps> = ({ defaultFormData, onSubmit }) => {
  const formik = useFormik({
    initialValues: {
      first_name: defaultFormData.first_name,
      last_name: defaultFormData.last_name,
    },
    validationSchema: object({
      first_name: string()
        .required('Required')
        .min(TEXT_LENGTH_LIMITS.MIN, `Min first name length is ${TEXT_LENGTH_LIMITS.MIN} characters`)
        .max(TEXT_LENGTH_LIMITS.MAX, `Max first name length is ${TEXT_LENGTH_LIMITS.MAX} characters`),
      last_name: string()
        .required('Required')
        .min(TEXT_LENGTH_LIMITS.MIN, `Min last name length is ${TEXT_LENGTH_LIMITS.MIN} characters`)
        .max(TEXT_LENGTH_LIMITS.MAX, `Max last name length is ${TEXT_LENGTH_LIMITS.MAX} characters`),
    }),
    onSubmit: async (values) => {
      const { first_name, last_name } = values;
      await onSubmit({ first_name, last_name });
    },
  });

  const isDisabledBtn = Boolean((formik.touched.first_name && formik.errors.first_name) || (formik.touched.last_name && formik.errors.last_name));

  const resetNewData = () => {
    formik.resetForm()
  }

  return (
    <form className='c-profile-info-form' onSubmit={formik.handleSubmit} >
      <div className="flex flex-col gap-5.5 sm:flex-row">
        <div className="w-full sm:w-1/2">
          <InputControl
            label='First name'
            type="text"
            name='first_name'
            placeholder="Enter your first name"
            value={formik.values.first_name}
            customOnChange={formik.handleChange}
            onBlur={formik.handleBlur}
            showError={formik.touched.first_name && formik.errors.first_name}
            errorText={formik.errors.first_name}
          />
        </div>
        <div className="w-full sm:w-1/2">
          <InputControl
            label='Last name'
            type="text"
            name='last_name'
            placeholder="Enter your last name"
            value={formik.values.last_name}
            customOnChange={formik.handleChange}
            onBlur={formik.handleBlur}
            showError={formik.touched.last_name && formik.errors.last_name}
            errorText={formik.errors.last_name}
          />
        </div>
      </div>
      <div className="flex justify-end gap-4.5">
        <InteractiveButton
          text='Cancel'
          customType='bg-zinc-100 text-black border border-stroke border-zinc-300 hover:bg-zinc-200 hover:border-zinc-300 disabled:bg-zinc-400 disabled:border-zinc-600'
          className="text-black"
          onClick={resetNewData}
          disabled={formik.isSubmitting}
        />
        <InteractiveButton
          text='Save'
          submit
          disabled={isDisabledBtn}
          isLoading={formik.isSubmitting}
        />
      </div>
    </form>
  )
}

export default ProfileInfoForm;
